<script lang="ts" setup>
import { Delete, Loading, Upload } from '@element-plus/icons-vue'
import { DialogModal, ImageIcon } from '@metapic-apps/shared'
import type { UploadFile, UploadInstance, UploadRawFile } from 'element-plus'
import { ElMessage, ElNotification } from 'element-plus'
import { computed, ref, useSlots } from 'vue'
import { Cropper } from 'vue-advanced-cropper'

import type { ProcessableImageType } from '@/types/common.types'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import ImageComponent from '@/ui-library/atoms/ImageComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

import 'vue-advanced-cropper/dist/style.css'

const slots = useSlots()
const props = withDefaults(
  defineProps<{
    image?: ProcessableImageType
    label?: string
    placeholder?: string
    maximumSize?: number
    validTypes?: string[]
    autoUpload?: boolean
    aspectRatio?: number
    accept?: string
  }>(),
  {
    autoUpload: true,
    maximumSize: 10,
    validTypes: () => ['image/jpeg', 'image/png'],
    aspectRatio: 16 / 9,
    accept: 'image/jpeg, image/png',
  },
)
const imageThumbnail = defineModel<UploadRawFile | Blob>('imageThumbnail')

const cropperRef = ref<InstanceType<typeof Cropper>>()
const fileList = ref<UploadFile[]>([])
const uploadRef = ref<UploadInstance>()

const isLoading = ref(false)
const isCropperVisible = ref(false)

const thumbnail = computed(() => (imageThumbnail.value ? URL.createObjectURL(imageThumbnail.value) : null))

const handleLogoError = (e: Error) => {
  ElNotification.error({
    message: 'message' in e ? e.message : 'Oops! Something went wrong.',
    duration: 0,
  })
  isLoading.value = false
}

const handleChange = (file: UploadFile) => {
  const rawFile = file.raw

  if (rawFile) {
    if (!props.validTypes.includes(rawFile.type)) {
      ElMessage.error('The image must be in JPG or PNG format')
      uploadRef.value.clearFiles()
      return false
    } else if (rawFile.size / 1024 / 1024 > props.maximumSize) {
      ElMessage.error(`The size of the image can not exceed ${props.maximumSize}mb`)
      uploadRef.value.clearFiles()
      return false
    }

    if (thumbnail.value) {
      URL.revokeObjectURL(thumbnail.value)
    }
    imageThumbnail.value = rawFile
    isCropperVisible.value = true
  }
}

const beforeLogoUpload = () => {
  isLoading.value = true
  return true
}

const handleSuccess = () => {
  isLoading.value = false
}

const removeThumbnail = (e: MouseEvent) => {
  e.stopPropagation()
  uploadRef.value.clearFiles()
  isLoading.value = false
  imageThumbnail.value = undefined
}

const closeCropper = () => {
  isCropperVisible.value = false
  imageThumbnail.value = undefined
  uploadRef.value.clearFiles()
}

const handleCrop = () => {
  if (cropperRef.value && imageThumbnail.value) {
    const { canvas } = cropperRef.value.getResult()
    if (canvas) {
      canvas.toBlob((blob) => {
        if (blob) {
          imageThumbnail.value = blob
          isCropperVisible.value = false
          uploadRef.value.clearFiles()
        }
      }, imageThumbnail.value.type)
    }
  }
}
</script>
<template>
  <div>
    <slot v-if="slots.label" name="label" />
    <TypographyComponent v-else-if="label" variant="title-large">{{ label }}</TypographyComponent>
    <el-form-item prop="logo">
      <el-upload
        ref="uploadRef"
        v-model:file-list="fileList"
        :accept="accept"
        class="w-100"
        drag
        action="#"
        name="logo"
        :limit="1"
        :show-file-list="false"
        :on-success="handleSuccess"
        :on-error="handleLogoError"
        :auto-upload="autoUpload"
        :before-upload="beforeLogoUpload"
        :on-change="handleChange"
      >
        <FlexComponent :gap="16" basis="100%" class="upload-input__wrapper">
          <FlexComponent align="center" justify="center" shrink="0" class="upload-input__container">
            <template v-if="thumbnail">
              <img :src="thumbnail" class="upload-input__thumbnail" alt="store logo" />
              <span class="upload-input__thumbnail-actions" @click.prevent="removeThumbnail">
                <el-icon :size="24" class="cursor-pointer">
                  <Delete />
                </el-icon>
              </span>
            </template>
            <template v-else-if="image">
              <ImageComponent :image="image" :width="170" :height="96" alt="store logo" class="upload-input__thumbnail" />
            </template>
            <el-icon v-else :class="{ 'is-loading': isLoading }" :size="24">
              <Loading v-if="isLoading" />
              <ImageIcon v-else />
            </el-icon>
          </FlexComponent>
          <FlexComponent flex="1" direction="column" justify="center" basis="100%">
            <div class="el-upload__text">
              <slot v-if="slots.placeholder" name="placeholder" />
              <TypographyComponent v-else-if="placeholder" variant="body-small" color="rgba(3, 30, 81, 0.64)">
                {{ placeholder }}
              </TypographyComponent>
            </div>
            <div>
              <el-button round :icon="Upload"> Browse </el-button>
            </div>
          </FlexComponent>
        </FlexComponent>
      </el-upload>
    </el-form-item>
    <DialogModal v-model="isCropperVisible" width="600px" size="auto" direction="btt" :before-close="closeCropper">
      <Cropper
        ref="cropperRef"
        class="upload-input__cropper"
        background-class="upload-input__cropper-background"
        :src="thumbnail"
        :stencil-props="{ aspectRatio }"
      />
      <template #footer>
        <FlexComponent justify="flex-end" :gap="16">
          <el-button size="large" @click="closeCropper">Cancel</el-button>
          <ButtonComponent variant="accent" @click="handleCrop">Crop image</ButtonComponent>
        </FlexComponent>
      </template>
    </DialogModal>
  </div>
</template>
<style lang="scss" scoped>
$breakpoint-lg: 992px;
:deep(.el-upload-dragger) {
  --el-upload-dragger-padding-horizontal: 16px;
  --el-color-primary: var(--el-border-color);
  border: 1px solid var(--el-border-color);

  &:hover {
    box-shadow: 0 0 0 1px var(--el-border-color-hover) inset;
  }
}
.upload-input__wrapper {
  flex-wrap: wrap;
  justify-content: center;
  @media (min-width: $breakpoint-lg) {
    flex-wrap: initial;
    justify-content: initial;
  }
}
.upload-input__container {
  position: relative;
  width: 170px;
  height: 96px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px dashed rgba(3, 30, 81, 0.24);

  &:hover > img + .upload-input__thumbnail-actions {
    opacity: 1;
  }
}

.upload-input__thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-input__thumbnail-actions {
  background: #{var(--black04)};
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  z-index: 2;
  opacity: 0;
  cursor: pointer;

  :deep(.el-icon path) {
    fill: #fff;
  }
}

.upload-button {
  background: #fff;

  &:hover,
  &:focus,
  &:active {
    background: #000000;

    :deep(path) {
      fill: #fff;
    }
  }
}

.upload-input__cropper {
  max-height: 600px;
  margin: 24px 0;
  background: #fff;
}
:deep(.upload-input__cropper-background) {
  background: #fff;
}
</style>
